import React, { useState, useEffect, useCallback } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import MasterTabs from "../Master/MasterTabs";

const initialFieldValues = {
  userId: "00000000-0000-0000-0000-000000000000",
  branchId: "00000000-0000-0000-0000-000000000000",
  name: "",
  userName: "",
  password: "",
  email: "",
  phoneNumber: "",
  roleName: "STAFF",
  isActive: true,
};

const Users = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addShow, setAddShow] = useState(false);
  const [changeShow, setChangeShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToChangeStatus, setItemToChangeStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Search and filter states - similar to Clients page
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleOpenStatusModal = (user) => {
    setItemToChangeStatus(user);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setItemToChangeStatus(null);
  };

  const handleAddClose = () => {
    setAddShow(false);
    setRecordForEdit(null);
  };

  const handleAddShow = () => {
    setRecordForEdit(null);
    setValues(initialFieldValues);
    setAddShow(true);
  };

  const handleChangeClose = () => {
    setChangeShow(false);
    setRecordForEdit(null);
  };

  const handleChangeShow = (data) => {
    setValues(data);
    setChangeShow(true);
  };

  useEffect(() => {
    if (recordForEdit !== null) {
      setValues(recordForEdit);
    }
  }, [recordForEdit]);

  const GetBranches = async (pageNumber) => {
    const res = await apiService.get(
      `${config.GETBRANCHBYCOMPANY}/${user?.cid}`,
      pageSize,
      pageNumber
    );
    setBranches(res.data);
  };

  // Updated GetData to include search and filters - similar to Clients page
  const GetData = async (currentPage = 1, search = "", status = "") => {
    if (!user) return;

    try {
      // Build query parameters like Clients page
      let apiUrl = `${config.GETUSERSBYCOMPANY}/${user?.cid}?roleName=STAFF&PageNumber=${currentPage}&PageSize=${pageSize}`;

      // Add search term if provided
      if (search) {
        apiUrl += `&searchTerm=${encodeURIComponent(search)}`;
      }

      // Add status filter if provided
      if (status !== "") {
        apiUrl += `&isActive=${status}`;
      }

      const response = await apiService.getWithParams(apiUrl);

      console.log("Fetched staff users:", response);
      const usersData = response.data || [];
      setLists(usersData);
      setFilteredLists(usersData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
      setPageSize(response.pageSize || 20);
    } catch (error) {
      console.error("Failed to fetch staff users:", error);
      setLists([]);
      setFilteredLists([]);
    }
  };

  // Generate search suggestions - similar to Clients page
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const nameMatches = lists
      .filter((user) => user.name?.toLowerCase().includes(searchLower))
      .slice(0, 5);

    const emailMatches = lists
      .filter(
        (user) =>
          user.email?.toLowerCase().includes(searchLower) &&
          !user.name?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const phoneMatches = lists
      .filter(
        (user) =>
          user.phoneNumber?.includes(searchValue) &&
          !user.name?.toLowerCase().includes(searchLower) &&
          !user.email?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [...nameMatches, ...emailMatches, ...phoneMatches];
    setSuggestions(allSuggestions);
  };

  // Handle search - similar to Clients page
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredLists(lists);
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = lists.filter(
        (user) =>
          user.name?.toLowerCase().includes(value.toLowerCase()) ||
          user.email?.toLowerCase().includes(value.toLowerCase()) ||
          user.phoneNumber?.includes(value)
      );
      setFilteredLists(filtered);
    }
  };

  // Handle suggestion click - similar to Clients page
  const handleSuggestionClick = (user) => {
    setSearchTerm(user.name);
    setFilteredLists([user]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Handle status filter change
  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setPageNumber(1);
    GetData(1, searchTerm, value);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setPageNumber(1);
    GetData(1, "", "");
  };

  const GetLastPageData = () => {
    GetData(totalPages, searchTerm, statusFilter);
  };

  const GetFirstPageData = () => {
    GetData(1, searchTerm, statusFilter);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number, searchTerm, statusFilter);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        id={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  const showEditDetails = (data) => {
    setRecordForEdit(data);
    setValues(data);
    setAddShow(true);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETEUSER, itemToDelete);
        await GetData(pageNumber, searchTerm, statusFilter);
      } catch (error) {
        // Error toast already handled by apiService
      }
      handleCloseDeleteModal();
    }
  };

  const handleStatusChange = async () => {
    if (itemToChangeStatus !== null) {
      try {
        const updatedUser = {
          ...itemToChangeStatus,
          isActive: !itemToChangeStatus.isActive,
        };

        await apiService.update(config.UPDATEUSER, updatedUser);
        await GetData(pageNumber, searchTerm, statusFilter);
      } catch (error) {
        // Error toast already handled by apiService
      }
      handleCloseStatusModal();
    }
  };

  const validate = () => {
    let temp = {};
    temp.branchId =
      values.branchId === "00000000-0000-0000-0000-000000000000" ? false : true;
    temp.name = values.name === "" ? false : true;
    temp.phoneNumber = values.phoneNumber === "" ? false : true;
    temp.password = values.password === "" ? false : true;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      var formData = {
        userId: values.userId,
        userName: values.phoneNumber,
        password: values.password,
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        branchId: values.branchId,
        roleName: "STAFF", // Static role name
        isActive:
          values.isActive === "true"
            ? true
            : values.isActive === true
            ? true
            : false,
      };
      try {
        const isNew =
          formData.userId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATEUSER, formData);
        } else {
          await apiService.update(config.UPDATEUSER, formData);
        }
        await GetData(pageNumber, searchTerm, statusFilter);
        resetForm();
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      var formData = {
        userId: values.userId,
        oldpassword: values.password,
        newpassword: values.password,
      };
      try {
        const isNew =
          formData.userId === "00000000-0000-0000-0000-000000000000";

        if (!isNew) {
          await apiService.create(config.CHANGEPASSWORD, formData);
        }
        resetForm();
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setAddShow(false);
    setBtnLoading(false);
    setChangeShow(false);
    setShowPassword(false);
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber, searchTerm, statusFilter);
      GetBranches(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <MasterTabs pageName="USER" />
                </div>

                {/* Search and Filter Section - Similar to Clients page */}
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="card-title mb-0">Staff Management</h5>
                        <button
                          className="btn btn-primary"
                          onClick={handleAddShow}
                        >
                          <i className="ri-add-line me-2"></i>
                          Add Staff
                        </button>
                      </div>

                      {/* Filter Section */}
                      <div
                        className="row align-items-end mb-4"
                        style={{ gap: "8px 0" }}
                      >
                        {/* Search Bar - wider column */}
                        <div className="col-md-6">
                          <div style={{ position: "relative" }}>
                            <div style={{ position: "relative" }}>
                              <i
                                className="ri-search-line"
                                style={{
                                  position: "absolute",
                                  left: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  zIndex: 10,
                                  color: "#6c757d",
                                }}
                              ></i>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, email, or phone..."
                                value={searchTerm}
                                onChange={handleSearch}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                                style={{
                                  height: "45px",
                                  paddingLeft: "40px",
                                  position: "relative",
                                }}
                              />
                              {searchTerm && (
                                <button
                                  className="btn position-absolute end-0 top-0 bottom-0"
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    zIndex: 10,
                                  }}
                                  onClick={() => setSearchTerm("")}
                                >
                                  <i className="ri-close-line fs-5"></i>
                                </button>
                              )}

                              {/* Suggestions dropdown */}
                              {showSuggestions && suggestions.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    width: "100%",
                                    marginTop: "4px",
                                    border: "1px solid #dee2e6",
                                    backgroundColor: "white",
                                    borderRadius: "6px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    zIndex: 1000,
                                  }}
                                >
                                  {suggestions.map((user) => (
                                    <div
                                      key={user.userId}
                                      style={{
                                        padding: "8px 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        backgroundColor: "white",
                                        transition: "background-color 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor =
                                          "#f8f9fa";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor =
                                          "white";
                                      }}
                                      onClick={() =>
                                        handleSuggestionClick(user)
                                      }
                                    >
                                      <i className="ri-user-line me-2 text-secondary"></i>
                                      <div>
                                        <div style={{ fontWeight: "600" }}>
                                          {user.name}
                                        </div>
                                        {user.email && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {user.email}
                                          </div>
                                        )}
                                        {user.phoneNumber && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {user.phoneNumber}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-2">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            style={{ height: "45px" }}
                          >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </div>

                        {/* Reset Button */}
                        <div className="col-md-1">
                          <button
                            className="btn btn-danger w-100"
                            onClick={handleResetFilters}
                            style={{ height: "45px" }}
                          >
                            <i className="ri-refresh-line me-1"></i>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table Section */}
                  <div className="mt-4">
                    <div className="table-responsive table-card p-2">
                      <table className="table align-middle" id="customerTable">
                        <thead className="table-light">
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Branch</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {filteredLists.length > 0 ? (
                            filteredLists.map((v, index) => (
                              <tr key={v.userId}>
                                <td>
                                  {(pageNumber - 1) * pageSize + index + 1}
                                </td>
                                <td>{v.name}</td>
                                <td>{v.userName}</td>
                                <td>{v.email}</td>
                                <td>{v.phoneNumber}</td>
                                <td>{v.branchName}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      v.isActive ? "bg-success" : "bg-danger"
                                    } fs-6`}
                                  >
                                    {v.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <ul className="list-inline hstack gap-2 mb-0">
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="Change Status"
                                    >
                                      {v.branchName !== "MAIN" && (
                                        <Link
                                          className="edit-item-btn"
                                          onClick={() =>
                                            handleOpenStatusModal(v)
                                          }
                                        >
                                          <i
                                            className={`fs-5 ${
                                              v.isActive
                                                ? "ri-user-unfollow-line text-danger"
                                                : "ri-user-follow-line text-success"
                                            }`}
                                          />
                                        </Link>
                                      )}
                                    </li>
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="Edit"
                                    >
                                      <Link
                                        className="edit-item-btn"
                                        onClick={() => showEditDetails(v)}
                                      >
                                        <i className="ri-edit-line align-bottom text-muted" />
                                      </Link>
                                    </li>
                                  </ul>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="ri-user-search-line fs-1"></i>
                                  <p className="mt-2">
                                    {searchTerm || statusFilter
                                      ? "No staff members found matching your criteria."
                                      : "No staff members found."}
                                  </p>
                                  {(searchTerm || statusFilter) && (
                                    <button
                                      className="btn btn-sm btn-outline-primary mt-2"
                                      onClick={handleResetFilters}
                                    >
                                      Clear Filters
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {filteredLists.length > 0 && (
                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                      <div className="flex-shrink-0">
                        <div className="text-muted">
                          Showing{" "}
                          <span className="fw-semibold">
                            {filteredLists.length}
                          </span>{" "}
                          of <span className="fw-semibold">{totalRecords}</span>{" "}
                          Staff Members
                        </div>
                      </div>
                      <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li
                          className={`page-item ${
                            pageNumber === 1 ? "disabled" : ""
                          }`}
                          onClick={() => pageNumber !== 1 && GetFirstPageData()}
                        >
                          <Link className="page-link">First</Link>
                        </li>
                        {renderPageNumbers}
                        <li
                          className={`page-item ${
                            pageNumber === totalPages ? "disabled" : ""
                          }`}
                          onClick={() =>
                            pageNumber !== totalPages && GetLastPageData()
                          }
                        >
                          <Link className="page-link">Last</Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit User Modal */}
        <Modal show={addShow} onHide={handleAddClose} size="lg">
          <Modal.Header>
            <Modal.Title>
              {values.userId === "00000000-0000-0000-0000-000000000000"
                ? "Add"
                : "Edit"}{" "}
              Staff
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label htmlFor="name" className="form-label">
                    Branch
                  </label>
                  <select
                    value={values.branchId}
                    name="branchId"
                    onChange={handleInputChange}
                    className={"form-select" + applyErrorClass("branchId")}
                  >
                    <option value="00000000-0000-0000-0000-000000000000">
                      Please select branch
                    </option>
                    {branches.length > 0 &&
                      branches.map((b) => (
                        <option key={b.branchId} value={b.branchId}>
                          {b.branchName}
                        </option>
                      ))}
                  </select>
                  {errors.branchId === false ? (
                    <div className="validationerror">Please select branch </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-lg-6 mb-3">
                  <label htmlFor="name" className="form-label">
                    Name
                  </label>
                  <input
                    type="text"
                    value={values.name}
                    name="name"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("name")}
                    placeholder="Please Enter Name"
                    maxLength={45}
                  />
                  {errors.name === false ? (
                    <div className="validationerror">Please enter name </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-lg-6 mb-3">
                  <label htmlFor="phoneNumber" className="form-label">
                    Mobile (Username)
                  </label>
                  <input
                    type="text"
                    value={values.phoneNumber}
                    name="phoneNumber"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("phoneNumber")}
                    placeholder="Please Enter Phone"
                    maxLength={15}
                  />
                  {errors.phoneNumber === false ? (
                    <div className="validationerror">Please enter phone </div>
                  ) : (
                    ""
                  )}
                </div>
                {values.userId === "00000000-0000-0000-0000-000000000000" ? (
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="name" className="form-label">
                      Password
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        maxLength={45}
                        value={values.password}
                        name="password"
                        onChange={handleInputChange}
                        className={"form-control" + applyErrorClass("password")}
                        placeholder="Please Enter Password"
                      />
                      <span
                        className="position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                        }}
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? (
                          <i className="ri-eye-line"></i>
                        ) : (
                          <i className="ri-eye-off-line"></i>
                        )}
                      </span>
                    </div>
                    {errors.password === false ? (
                      <div className="validationerror">
                        Please enter password{" "}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  ""
                )}
                <div className="col-lg-6 mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    value={values.email}
                    name="email"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("email")}
                    placeholder="Please Enter Email"
                    maxLength={45}
                  />
                  {errors.email === false ? (
                    <div className="validationerror">Please enter email </div>
                  ) : (
                    ""
                  )}
                </div>

                <Modal.Footer>
                  <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                    {btnLoading === false ? (
                      <button type="submit" className="btn btn-primary">
                        {values.userId ===
                        "00000000-0000-0000-0000-000000000000"
                          ? "Submit"
                          : "Update"}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled
                      >
                        Please wait...
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </Modal.Footer>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* Other modals remain the same */}
        <Modal show={changeShow} onHide={handleChangeClose}>
          <Modal.Header>
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleChangePassword} autoComplete="off" noValidate>
              <div className="row">
                <div className="col-lg-12 mb-3">
                  <label htmlFor="name" className="form-label">
                    Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      maxLength={45}
                      value={values.password}
                      name="password"
                      onChange={handleInputChange}
                      className={"form-control" + applyErrorClass("password")}
                      placeholder="Please Enter Password"
                    />
                    <span
                      className="position-absolute"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <i className="ri-eye-line"></i>
                      ) : (
                        <i className="ri-eye-off-line"></i>
                      )}
                    </span>
                  </div>
                  {errors.password === false ? (
                    <div className="validationerror">
                      Please enter password{" "}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <Modal.Footer>
                  <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                    {btnLoading === false ? (
                      <button type="submit" className="btn btn-primary">
                        Change Password
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled
                      >
                        Please wait...
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </Modal.Footer>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
          <Modal.Body className="text-center p-5">
            <div className="mb-4">
              <h4>
                {itemToChangeStatus?.isActive ? "Inactive User" : "Active User"}
              </h4>
              <p className="text-muted mt-2">
                Are you sure you want to{" "}
                {itemToChangeStatus?.isActive ? "inactive" : "active"} user{" "}
                <strong>{itemToChangeStatus?.name}</strong>?
              </p>
            </div>
            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleCloseStatusModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  itemToChangeStatus?.isActive ? "btn-primary" : "btn-success"
                }`}
                onClick={handleStatusChange}
              >
                {itemToChangeStatus?.isActive ? "Inactive" : "Active"}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>

      <style>{`
        .badge.bg-success {
          background: linear-gradient(45deg, #198754, #2ea65c);
        }
        .badge.bg-danger {
          background: linear-gradient(45deg, #dc3545, #e63749);
        }
        .badge.bg-info {
          background: linear-gradient(45deg, #0dcaf0, #25c2e9);
        }
        .badge {
          padding: 0.5em 0.8em;
          border-radius: 20px;
          font-weight: 500;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        
        .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default Users;
